import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Loader2, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface GiftSuggestionsProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
  embedded?: boolean;
  onSuggestionsAdded?: () => void;
}

export function GiftSuggestions({ onNavigate, giftGroupId, embedded = false, onSuggestionsAdded }: GiftSuggestionsProps) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualGift, setManualGift] = useState({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    url: ''
  });

  // Mark manual suggestions as viewed when component mounts
  useEffect(() => {
    const markManualViewed = async () => {
      if (!giftGroupId) return;
      
      try {
        await api.markManualViewed(giftGroupId);
        console.log('Marked manual suggestions as viewed');
      } catch (error) {
        console.error('Failed to mark manual viewed:', error);
        // Don't show error toast - this is a background action
      }
    };
    
    markManualViewed();
  }, [giftGroupId]);

  const handleQuickAdd = async () => {
    if (!url.trim() || !giftGroupId) return;
      
    try {
      setLoading(true);
      toast.loading('Scraping product details...', { id: 'scraping' });
      
      // Let backend scrape the URL
      const response = await api.addGiftSuggestion(giftGroupId, {
        url: url,
        source: 'manual'
      });

      toast.dismiss('scraping');

      // Check if backend says editing is needed (when scraping failed or got incomplete data)
      if (response.needsEditing || !response.success) {
        // Show dialog for user to complete missing details
        setManualGift({
          name: response.suggestion?.name || '',
          description: response.suggestion?.description || '',
          price: response.suggestion?.price ? response.suggestion.price.toString() : '',
          imageUrl: response.suggestion?.imageUrl || '',
          url: url
        });
        setShowManualForm(true);
        const errorMsg = response.error || 'Please complete the product details';
        toast.info(errorMsg);
      } else {
        // Scraped successfully
        setUrl('');
        toast.success('Gift suggestion added! View it in the Rankings tab.');
        if (onSuggestionsAdded) onSuggestionsAdded();
      }
    } catch (error) {
      toast.dismiss('scraping');
      console.error('Failed to add gift:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to add gift suggestion', {
        description: errorMessage.includes('no such table') 
          ? 'Database tables not set up. Please run the migration first.'
          : errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddGift = async () => {
    if (!manualGift.url.trim() || !giftGroupId) return;
    
    // Validate required fields
    if (!manualGift.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    
    if (!manualGift.price || parseFloat(manualGift.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
      
    try {
      setLoading(true);
      
      await api.addGiftSuggestion(giftGroupId, {
        url: manualGift.url,
        name: manualGift.name,
        description: manualGift.description,
        price: parseFloat(manualGift.price),
        imageUrl: manualGift.imageUrl,
        source: 'manual'
      });

      setUrl('');
      setManualGift({ name: '', description: '', price: '', imageUrl: '', url: '' });
      setShowManualForm(false);
      toast.success('Gift suggestion added! View it in the Rankings tab.');
      if (onSuggestionsAdded) onSuggestionsAdded();
    } catch (error) {
      console.error('Failed to add gift:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to add gift suggestion', {
        description: errorMessage.includes('no such table') 
          ? 'Database tables not set up. Please run the migration first.'
          : errorMessage
      });
    } finally {
      setLoading(false);
    }
  };



  if (!giftGroupId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <h2 className="text-xl mb-2">No Gift Group Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a gift group first</p>
          <Button onClick={() => onNavigate('dashboard', { tab: 'todo' })}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Card className="p-6 border-2 border-primary/20 mb-6 shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-primary mb-2">Gift Suggestions</h1>
              <h2 className="text-xl text-muted-foreground">Suggest gifts for the group</h2>
            </div>
            <Badge variant="outline" className="text-xs">v2.0</Badge>
          </div>
        </Card>
      )}

        {/* Add New Gift */}
        <Card className="p-6 border-2 border-primary/20 mb-6 shadow-lg">
          <h3 className="text-lg mb-4 text-primary">Add Gift Suggestion</h3>
          <div className="flex gap-3">
            <Input
              placeholder="Paste product URL from Amazon, Target, etc..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleQuickAdd()}
              className="flex-1"
            />
            <Button 
              onClick={handleQuickAdd}
              disabled={!url.trim() || loading}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scraping...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Gift
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            🔗 Paste any product URL and we'll automatically extract the details. You can edit them if needed.
          </p>
        </Card>

        {/* Edit Gift Dialog (shown when scraping is incomplete) */}
        <Dialog open={showManualForm} onOpenChange={setShowManualForm}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Complete Gift Details</DialogTitle>
              <DialogDescription>
                We couldn't extract all the details. Please fill in the missing information.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="url">Product URL *</Label>
                <Input
                  id="url"
                  value={manualGift.url}
                  onChange={(e) => setManualGift({ ...manualGift, url: e.target.value })}
                  placeholder="https://www.amazon.com/..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={manualGift.name}
                  onChange={(e) => setManualGift({ ...manualGift, name: e.target.value })}
                  placeholder="e.g., Sony WH-1000XM5 Headphones"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={manualGift.description}
                  onChange={(e) => setManualGift({ ...manualGift, description: e.target.value })}
                  placeholder="Brief description of the product..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price (USD) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={manualGift.price}
                  onChange={(e) => setManualGift({ ...manualGift, price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowManualForm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddGift} 
                disabled={loading || !manualGift.url.trim() || !manualGift.name.trim() || !manualGift.price}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Suggestion
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* URL Scraper Preview Info */}
        <Card className="border-2 border-blue-200 bg-blue-50 mb-6">
          <div className="p-6">
            <h3 className="text-lg mb-3 text-primary flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              How URL Scraping Works
            </h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">1</div>
                <div>
                  <p className="text-primary">Paste any product URL</p>
                  <p className="text-xs">Works with Amazon, Target, Walmart, Best Buy, eBay, and most e-commerce sites</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">2</div>
                <div>
                  <p className="text-primary">We automatically extract details</p>
                  <p className="text-xs">Product name, description, price, and image are pulled from the page</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">3</div>
                <div>
                  <p className="text-primary">Review and edit if needed</p>
                  <p className="text-xs">If something's missing, we'll open a form for you to complete the details</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">4</div>
                <div>
                  <p className="text-primary">Add to group rankings</p>
                  <p className="text-xs">Your suggestion appears in the Gift Rankings view where everyone can rank it</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {!embedded && (
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                onClick={() => onNavigate('ai-gift-suggestions', { giftGroupId })}
              >
                Add AI Suggestions
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={() => onNavigate('gift-group-overview', { giftGroupId })}
              >
                Go to Rankings
              </Button>
            </div>
          </div>
        )}
      </>
  );
  
  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto">
        {content}
      </div>
    </div>
  );
}